import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import UpgradeModal from './billing/UpgradeModal';
import {useBusiness} from '../contexts/BusinessContext';
import {supabase} from '../lib/supabase/client';
import {getCurrentSubscription} from '../lib/billing/billingService';
import {useLanguage} from '../contexts/LanguageContext';
import {
  getPlanLimit,
  normalizePlanType
} from '../lib/billing/planLimits';
import {hasGooglePlayReviewerEntitlement} from '../lib/billing/businessFeatureService';

const {FiBriefcase, FiChevronDown, FiPlus, FiCheck, FiX} = FiIcons;

function accessLabel(business) {
  if (business._accessMode === 'accountant') return 'Accountant access';
  if (business._accessMode === 'member') return 'Team access';
  return 'Owner workspace';
}

function BusinessList({
  businesses,
  activeBusiness,
  onSwitch,
  onClose,
  onAddBusiness,
  canAddBusiness,
  t
}) {
  return (
    <div className="space-y-1">
      {businesses.map((business) => {
        const isActive = business.id === activeBusiness?.id;

        return (
          <button
            key={business.id}
            type="button"
            aria-pressed={isActive}
            onClick={() => {
              onSwitch(business.id);
              onClose();
            }}
            className={`flex w-full items-center gap-3 rounded-xl border-l-4 px-3 py-3 text-left transition ${
              isActive
                ? 'border-primary bg-mint'
                : 'border-transparent hover:bg-slate-50'
            }`}
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary text-sm font-bold text-white">
              {business.name?.charAt(0).toUpperCase() || 'B'}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
              <span className="block truncate">{business.name || t('Unnamed Business')}</span>
              <span className="mt-0.5 block text-[11px] font-medium text-slate-600">
                {t(accessLabel(business))}
              </span>
            </span>
            {isActive && (
              <span className="shrink-0 text-primary"><SafeIcon icon={FiCheck} className="h-4 w-4" /><span className="sr-only">{t('Your Business')}</span></span>
            )}
          </button>
        );
      })}

      {canAddBusiness && <button
        type="button"
        onClick={onAddBusiness}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-semibold text-primary hover:bg-mint"
      >
        <SafeIcon icon={FiPlus} className="h-4 w-4" />
        {t('Add a Business')}
      </button>}
    </div>
  );
}

function BusinessSwitcher({compact = false}) {
  const {activeBusiness, activeAccess, businesses, switchBusiness, userId, isLoading} = useBusiness();
  const navigate = useNavigate();
  const {t} = useLanguage();
  const [open, setOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const subscriptionQuery = useQuery({
    queryKey: ['current-subscription', userId],
    queryFn: () => getCurrentSubscription(userId),
    enabled: Boolean(supabase && userId)
  });
  const reviewerEntitlementQuery = useQuery({
    queryKey: ['google-play-reviewer-entitlement', userId, activeBusiness?.id],
    queryFn: () => hasGooglePlayReviewerEntitlement(activeBusiness.id),
    enabled: Boolean(supabase && userId && activeBusiness?.id)
  });

  const planType = normalizePlanType(subscriptionQuery.data?.plan_type);
  const planLimit = getPlanLimit(planType);
  const ownedBusinesses=businesses.filter(item=>item.created_by===userId);
  const atLimit = ownedBusinesses.length >= planLimit;
  const reviewerStatusKnown=!activeBusiness || reviewerEntitlementQuery.isSuccess;
  const canAddBusiness=activeAccess?.mode!=='accountant'
    && reviewerStatusKnown
    && reviewerEntitlementQuery.data!==true;

  const handleAddBusiness = () => {
    if (atLimit) {
      setOpen(false);
      setUpgradeOpen(true);
      return;
    }

    setOpen(false);
    navigate('/businesses/new');
  };

  return (
    <>
      <button
        type="button"
        aria-label={activeBusiness
          ? `${t('Switch Business')}: ${activeBusiness.name}`
          : t('Choose a Business')}
        aria-expanded={open}
        aria-busy={isLoading}
        disabled={isLoading}
        onClick={() => setOpen(true)}
        className={`flex items-center rounded-xl border border-slate-200 bg-white text-left transition hover:border-primary/40 hover:bg-slate-50 ${
          compact ? 'w-full min-w-0 gap-2 px-3 py-2' : 'w-full gap-3 p-3'
        } disabled:cursor-wait disabled:hover:border-slate-200 disabled:hover:bg-white`}
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-mint text-primary">
          {isLoading ? (
            <span className="h-4 w-4 animate-pulse rounded-md bg-primary/20 motion-reduce:animate-none" aria-hidden="true" />
          ) : activeBusiness ? (
            <span className="text-sm font-bold">
              {activeBusiness.name.charAt(0).toUpperCase()}
            </span>
          ) : (
            <SafeIcon icon={FiBriefcase} className="h-4 w-4" />
          )}
        </span>

        <span className="min-w-0 flex-1" aria-hidden={isLoading || undefined}>
          {!compact && (
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600">
              {t('Your Business')}
            </span>
          )}
          {isLoading ? (
            <span className="mt-1 block h-4 w-28 max-w-full animate-pulse rounded-full bg-slate-200 motion-reduce:animate-none" />
          ) : (
            <span className="block truncate text-sm font-semibold text-ink">
              {activeBusiness?.name || t('Choose a Business')}
            </span>
          )}
        </span>

        {!isLoading && <SafeIcon icon={FiChevronDown} className="h-4 w-4 shrink-0 text-slate-400" />}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 lg:absolute lg:inset-auto lg:left-5 lg:top-24 lg:w-[250px]">
          <button
            type="button"
            aria-label={t('Close menu')}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-ink/40 lg:hidden"
          />

          <div className="absolute left-[calc(1rem+var(--app-safe-area-left))] right-[calc(1rem+var(--app-safe-area-right))] top-[calc(5rem+var(--app-safe-area-top))] rounded-card border border-slate-200 bg-white p-4 shadow-2xl lg:inset-auto lg:relative lg:top-0 lg:mx-0">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-bold text-ink">{t('Switch Business')}</h2>
                <p className="mt-1 text-xs leading-5 text-slate-600">{t('Choose a Business')}</p>
              </div>
              <button
                type="button"
                aria-label={t('Close')}
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
              >
                <SafeIcon icon={FiX} className="h-5 w-5" />
              </button>
            </div>

            {businesses.length > 0 ? (
              <BusinessList
                businesses={businesses}
                activeBusiness={activeBusiness}
                onSwitch={switchBusiness}
                onClose={() => setOpen(false)}
                onAddBusiness={handleAddBusiness}
                canAddBusiness={canAddBusiness}
                t={t}
              />
            ) : canAddBusiness ? (
              <button
                type="button"
                onClick={handleAddBusiness}
                className="block w-full rounded-xl bg-mint px-3 py-3 text-left text-sm font-semibold text-primary"
              >
                {t('Create your first Business')}
              </button>
            ) : <p className="rounded-xl bg-surface px-3 py-3 text-sm text-slate-500">{t('No accessible Businesses.')}</p>}
          </div>
        </div>
      )}

      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        planType={planType}
        businessCount={ownedBusinesses.length}
      />
    </>
  );
}

export default BusinessSwitcher;
